export const currencySymbolMap = {
  ZAR: "R",
  USD: "$",
  NGN: "₦",
  GHS: "GH₵",
  EUR: "€",
  GBP: "£",
};

export const moduleLabelMap = {
  companies: "Companies",
  invoicing: "Invoicing",
  manageEmployees: "Manage Employees",
  attendance: "Attendance",
  loans: "Loans",
  deductions: "Deductions",
  leaveManagement: "Leave Management",
  commission: "Commission",
  travelClaims: "Travel Claims",
  recruitment: "Recruitment",
  timesheet: "Timesheet",
  projects: "Projects",
  payroll: "Payroll",
  salaryManagement: "Salary Management",
  reports: "Reports",
};

export const moduleLimitKeysMap = {
  companies: ["companiesPerPlan"],
  invoicing: ["invoicesPerPlan", "invoiceValueCap"],
  manageEmployees: ["activeEmployees", "employeeDocsPerEmployee"],
  attendance: ["attendanceDaysPerPlan", "attendanceUsersPerPeriod"],
  loans: ["activeLoans"],
  deductions: ["activeDeductions"],
  leaveManagement: ["leaveRequestsPerPlan"],
  commission: ["commissionRunsPerPlan"],
  travelClaims: ["travelClaimsPerPlan", "allowancesPerPlan"],
  recruitment: ["recruitmentPerPlan"],
  timesheet: ["timesheetPerPlan"],
  projects: ["activeProjects"],
  payroll: ["payrollRunsPerPlan"],
  salaryManagement: ["payslipLimit"],
  reports: ["reportDownloadsPerPlan", "exportFormats"],
};

// Allowance types a topup pack may raise. `value` is the backend API value and
// shares the Plan.moduleLimits vocabulary — never rename it; only `label` is
// display text. Other numeric moduleLimits keys exist (attendanceDaysPerPlan,
// attendanceUsersPerPeriod, invoiceValueCap, employeeDocsPerEmployee,
// exportFormats) but are NOT documented as purchasable, so they are excluded.
export const topupAppliesToOptions = [
  { value: "activeEmployees", label: "Active Employees" },
  { value: "invoicesPerPlan", label: "Invoices" },
  { value: "payslipLimit", label: "Payslips" },
  { value: "activeProjects", label: "Projects" },
  { value: "companiesPerPlan", label: "Companies" },
  { value: "activeLoans", label: "Loans" },
  { value: "activeDeductions", label: "Deductions" },
  { value: "leaveRequestsPerPlan", label: "Leave Requests" },
  { value: "commissionRunsPerPlan", label: "Commission Runs" },
  { value: "travelClaimsPerPlan", label: "Travel Claims" },
  { value: "allowancesPerPlan", label: "Allowances" },
  { value: "recruitmentPerPlan", label: "Recruitment" },
  { value: "timesheetPerPlan", label: "Timesheet" },
  { value: "payrollRunsPerPlan", label: "Payroll Runs" },
  { value: "reportDownloadsPerPlan", label: "Report Downloads" },
  { value: "operatorsPerPlan", label: "Operators" },
];

// The two moduleLimits keys that support "unlimited" semantics: a Plan may leave
// them blank/omitted to mean unlimited for the billing period, and a Topup pack's
// allowances map may carry them as an explicit `null` to mean "this pack grants
// unlimited attendance while active." Every other allowance/limit key still
// requires a real positive number wherever it appears.
export const TOPUP_ATTENDANCE_ALLOWANCE_KEYS = ["attendanceDaysPerPlan", "attendanceUsersPerPeriod"];

export const isAttendanceAllowanceKey = (key) => TOPUP_ATTENDANCE_ALLOWANCE_KEYS.includes(key);

// The full set of keys a topup pack's `allowances` map may carry: the 16
// purchasable non-attendance keys above, plus attendanceDaysPerPlan (which
// supports a null "unlimited" value instead of a positive integer). Only
// attendanceDaysPerPlan is purchasable via a topup pack — attendanceUsersPerPeriod
// is a Plan.moduleLimits field only and is never offered here.
export const topupAllowanceOptions = [
  ...topupAppliesToOptions,
  { value: "attendanceDaysPerPlan", label: "Attendance Days", isAttendance: true },
];

export const topupAppliesToLabel = (value) =>
  topupAllowanceOptions.find((option) => option.value === value)?.label || value || "—";

// Renders one entry of a pack/purchase `allowances` map. `value === null` means
// unlimited (only ever true for the two attendance keys) — never rendered as 0.
export const formatAllowanceGrant = (key, value) => {
  const label = topupAppliesToLabel(key);
  if (value === null) return { label, display: UNLIMITED_LABEL };
  const numeric = Number(value);
  return { label, display: Number.isFinite(numeric) ? String(numeric) : "—" };
};

// Orders an `allowances` map's entries by the canonical topupAllowanceOptions
// order, so every render site (catalogue, purchases, sales, checkout dialog)
// lists the same pack's modules in the same order. Unknown keys sort last, in
// whatever order Object.entries returned them.
export const sortedAllowanceEntries = (allowances) => {
  const entries = Object.entries(allowances && typeof allowances === "object" ? allowances : {});
  const order = topupAllowanceOptions.map((option) => option.value);
  return entries.slice().sort((a, b) => {
    const indexA = order.indexOf(a[0]);
    const indexB = order.indexOf(b[0]);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

// The only pack types the contract defines.
export const TOPUP_PACK_TYPES = ["fixed", "per_unit"];

// The topup purchase lifecycle, exactly as the backend emits it (lowercase).
// `label` is display text only; `color` maps to the MUI Chip palette already
// used elsewhere in the SuperAdmin tables.
export const topupStatusOptions = [
  { value: "pending", label: "Pending", color: "warning" },
  { value: "active", label: "Active", color: "success" },
  { value: "expired", label: "Expired", color: "default" },
  { value: "cancelled", label: "Cancelled", color: "default" },
  { value: "failed", label: "Failed", color: "error" },
];

export const TOPUP_STATUSES = topupStatusOptions.map((option) => option.value);

// Statuses the backend counts toward totalRevenue. Documented here so the UI
// can explain the figure — never to recompute it client-side.
export const TOPUP_REVENUE_STATUSES = ["active", "expired"];

// How an uncapped allowance is written. No prior convention existed in the
// project — planHelpers simply omitted null limits from its feature list.
export const UNLIMITED_LABEL = "Unlimited";

// True when an allowance is unlimited from either source: the backend's explicit
// `isUnlimited` flag, or `combinedLimit` (or `limit`, the LIMIT_REACHED shape)
// coming back as an explicit `null`. The response never says which source caused
// it — a capped plan can still show unlimited combined attendance because of an
// active topup — so this is the one place that decides "unlimited", and every
// other helper here must go through it rather than re-deriving the answer.
export const isAllowanceUnlimited = (allowance) => {
  if (!allowance) return false;
  if (allowance.isUnlimited === true) return true;
  if ("combinedLimit" in allowance) return allowance.combinedLimit === null;
  if ("limit" in allowance) return allowance.limit === null;
  return false;
};

// Renders the limit side of one allowance from GET /topup/usage.
// `combinedLimit` is taken verbatim from the backend — this never computes
// planAllowance + activeTopupQuantity. An unlimited allowance reports the
// label, never 0; a missing/malformed limit reports an em dash, never 0.
export const formatAllowanceLimit = (allowance) => {
  if (!allowance) return "—";
  if (isAllowanceUnlimited(allowance)) return UNLIMITED_LABEL;

  const limit = allowance.combinedLimit;
  if (limit === null || limit === undefined || limit === "") return "—";

  const numeric = Number(limit);
  return Number.isFinite(numeric) ? String(numeric) : "—";
};

// "Plan 20 + 5 from topups" — the breakdown behind the combined limit. Returns
// an empty string when the allowance is unlimited or the parts are unknown.
export const formatAllowanceBreakdown = (allowance) => {
  if (!allowance || isAllowanceUnlimited(allowance)) return "";

  const plan = allowance.planAllowance;
  const topup = allowance.activeTopupQuantity;
  if (plan === null || plan === undefined) return "";

  const topupCount = Number(topup) || 0;
  if (topupCount <= 0) return `Plan includes ${plan}`;
  return `Plan includes ${plan} + ${topupCount} from topups`;
};

// An unlimited allowance must never carry a topup upsell.
export const allowanceSupportsTopup = (allowance) =>
  !!allowance && !isAllowanceUnlimited(allowance);

export const topupStatusMeta = (value) =>
  topupStatusOptions.find((option) => option.value === value) || {
    value,
    label: value || "—",
    color: "default",
  };

// Shared validation for the topup pack model, so the create/edit form and its
// tests agree on one rule set. Returns a { field: message } map; empty = valid.
// Applies identically on create and edit — PUT /topup/:id accepts `allowances`
// and treats it as a full replace, so the same "at least one, each one valid"
// rule has to hold every time the form is submitted, not just on creation.
export const validateTopupPack = (form = {}) => {
  const errors = {};
  const name = String(form.name || "").trim();
  const packType = form.packType;
  const allowances = form.allowances && typeof form.allowances === "object" ? form.allowances : {};
  const allowanceKeys = Object.keys(allowances);

  if (!name) {
    errors.name = "Name is required.";
  }

  if (allowanceKeys.length === 0) {
    errors.allowances = "Select at least one module this pack grants.";
  } else {
    allowanceKeys.forEach((key) => {
      const raw = allowances[key];
      const rawTrimmed = String(raw ?? "").trim();

      if (isAttendanceAllowanceKey(key)) {
        // Blank means unlimited — valid. A filled-in value must still be sane.
        if (rawTrimmed !== "") {
          const numeric = Number(rawTrimmed);
          if (!Number.isFinite(numeric) || numeric <= 0) {
            errors[`allowance_${key}`] = "Enter a whole number greater than 0, or leave blank for unlimited.";
          }
        }
        return;
      }

      const numeric = Number(rawTrimmed);
      if (rawTrimmed === "" || !Number.isFinite(numeric) || numeric <= 0) {
        errors[`allowance_${key}`] = "Enter a whole number greater than 0.";
      }
    });
  }

  if (!TOPUP_PACK_TYPES.includes(packType)) {
    errors.packType = "Pack type must be fixed or per_unit.";
    return errors;
  }

  // A fixed pack has no bundle-size field of its own — what it grants is
  // expressed entirely through each allowance's value in the map above.
  if (packType === "per_unit") {
    const min = Number(form.minQuantity);
    const max = Number(form.maxQuantity);
    if (String(form.minQuantity ?? "").trim() === "" || !Number.isFinite(min) || min <= 0) {
      errors.minQuantity = "Minimum quantity is required and must be greater than 0.";
    }
    if (String(form.maxQuantity ?? "").trim() === "" || !Number.isFinite(max) || max <= 0) {
      errors.maxQuantity = "Maximum quantity is required and must be greater than 0.";
    } else if (Number.isFinite(min) && max < min) {
      errors.maxQuantity = "Maximum quantity must be greater than or equal to the minimum.";
    }
  }

  return errors;
};

// Builds the POST/PUT body from form state. Only the fields relevant to the
// chosen packType are sent, and pricing is never included — it goes through the
// dedicated pricing endpoints.
//
// `allowances` is sent on both create and update: PUT /topup/:id accepts it, and
// treats it as a full replace ($set), not a merge. Callers must pass the pack's
// complete current allowance map in `form.allowances` (every module it grants,
// not only the ones touched this session) — this function has no way to detect
// a narrowed map, it just sends whatever it's given.
export const buildTopupPayload = (form = {}) => {
  const allowances = form.allowances && typeof form.allowances === "object" ? form.allowances : {};

  const payload = {
    name: String(form.name || "").trim(),
    packType: form.packType,
    availableToPlans: Array.isArray(form.availableToPlans) ? form.availableToPlans : [],
    allowances: Object.keys(allowances).reduce((acc, key) => {
      const rawTrimmed = String(allowances[key] ?? "").trim();
      acc[key] = rawTrimmed === "" ? null : Number(rawTrimmed);
      return acc;
    }, {}),
  };

  // A fixed pack has no top-level quantity: the bundle size is expressed
  // entirely through each allowance's value in `payload.allowances` above.
  if (form.packType === "per_unit") {
    payload.minQuantity = Number(form.minQuantity);
    payload.maxQuantity = Number(form.maxQuantity);
  }

  return payload;
};
